import React from "react";

/**
 * Keeps a fault inside Remi from taking the portal down with it.
 *
 * RemiContextProvider wraps the whole application and RemiWidget renders on every
 * authenticated screen, so an unhandled render error here would blank the entire portal —
 * claims, payments, patients, everything — not just the assistant. React unmounts the whole
 * tree when a render throws and nothing catches it.
 *
 * The trade this boundary makes is deliberate: a biller who loses Remi can still do their job.
 * A biller who loses the portal cannot. So anything that throws in here is contained, logged
 * to the console for whoever investigates, and the rest of the application carries on as if
 * the assistant were simply switched off.
 *
 * It renders nothing on failure. A broken assistant should be absent, not a red box following
 * the biller from screen to screen.
 */
export default class RemiErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // Console only. This runs in the billing portal, and an error object from the assistant
    // can carry rendered answer text — which may contain PHI — so it must not be sent to any
    // logging endpoint from here.
    console.error("Remi assistant failed and was disabled for this session.", error, info);
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}
